/* The art, once it has arrived: parsed, named, and turned into a file.
 *
 * NONE OF THIS IS VUE, and the split is worth stating. AsciiPanel.vue paints
 * the box and AsciiPanel.vue writes the download, and both need the same three
 * answers: where the colours change, what the file should be called, and what
 * the HTML looks like. Deciding those once, here, is the argument lib/meme.js
 * makes for layoutCaption() -- two renderers of one picture agree because only
 * one of them decides.
 *
 * It also puts them somewhere testable. tests/ascii_text_test.mjs drives all
 * of this with no browser and no jp2a, which is the half of this feature that
 * CI can actually check.
 *
 * (And there is a smaller reason the HTML lives out here. tests/test_palette.py
 * forbids raw hex in a .vue file, correctly: a component that names #fff has
 * opted out of the theme. The DOWNLOAD is the one place a literal is right --
 * it is opened off a disk with no stylesheet, so it cannot reference a token --
 * and putting it in a .js module is a truer answer than adding the component to
 * the rule's exception list, since it is not the component's styling.)
 */

const ESC = String.fromCharCode(27)

/* Where jp2a's colours change, as a list of {text, colour}.
 *
 * Only SGR is handled -- ESC [ ... m -- because that is all jp2a emits, and of
 * those only the true-colour foreground (38;2;R;G;B) and the reset. Anything
 * else DROPS its colour rather than being guessed at: an unrecognised sequence
 * losing its colour is a stripe that renders in the default ink, where a guess
 * would be litter in the middle of the picture.
 *
 * A plain render short-circuits to one segment, so the common case does no
 * work at all.
 */
export function parseAnsi(art) {
    const text = art || ""
    if (!text.includes(ESC)) {
        return [{text, colour: null}]
    }

    // Built here rather than at module scope. The honest version of the
    // reason: a /g regex carries lastIndex, so a shared one IS mutable state
    // -- but a `while (exec())` loop runs to a null return, and that resets
    // lastIndex, so this particular walk would leak nothing either way.
    // Measured: moving it to module scope passes every check in
    // tests/ascii_text_test.mjs. It stays local because it costs nothing and
    // removes the question, not because a bug was found.
    const sgr = new RegExp(ESC + "\\[([0-9;]*)m", "g")
    const out = []
    let colour = null
    let at = 0
    let m

    while ((m = sgr.exec(text)) !== null) {
        if (m.index > at) {
            out.push({text: text.slice(at, m.index), colour})
        }
        const parts = m[1].split(";").filter((x) => x !== "")
        if (!parts.length || parts[0] === "0") {
            colour = null
        } else if (parts[0] === "38" && parts[1] === "2" && parts.length >= 5) {
            colour = `rgb(${parts[2]},${parts[3]},${parts[4]})`
        }
        at = m.index + m[0].length
    }
    if (at < text.length) {
        out.push({text: text.slice(at), colour})
    }
    return out
}

/* A name that says where it came from.
 *
 * "ascii.txt" in a downloads folder is a file nobody can identify a week
 * later -- the same argument batchOutputName() makes for the versions table.
 * The extension is replaced rather than appended, so a JPEG does not become
 * holiday.jpg-ascii.txt.
 */
export function outputName(sourceName, ext) {
    const raw = String(sourceName || "picture")
    const stem = raw.replace(/\.[^.]+$/, "").slice(0, 60) || "picture"
    return `${stem}-ascii.${ext}`
}

function escapeHtml(str) {
    return str.replace(/[&<>]/g, (c) => (
        c === "&" ? "&amp;" : c === "<" ? "&lt;" : "&gt;"))
}

/* One self-contained HTML file, and everything about it follows from where it
 * gets opened: double-clicked, off a disk, no server and no network.
 *
 * So no stylesheet, no script and no webfont -- the same reasoning
 * galleryPage() records for the index.html inside a document's zip. A
 * monospace stack the machine already has, the colour inline on each span, and
 * the ground baked in, because art rendered for a dark background is a
 * negative on white.
 *
 * WHY HTML AT ALL, given the box already shows colour: of the three ways this
 * can leave the page, it is the only one where colour survives usefully. Plain
 * text has no colour to lose; text carrying ANSI codes is readable only in a
 * terminal; this opens in any browser and can be sent to somebody.
 */
export function asciiHtml({art, dark = false, fontSize = "10px", title = "ascii"} = {}) {
    const body = parseAnsi(art).map((seg) => {
        const text = escapeHtml(seg.text)
        return seg.colour ? `<span style="color:${seg.colour}">${text}</span>` : text
    }).join("")

    const ground = dark ? "#000" : "#fff"
    const ink = dark ? "#fff" : "#000"

    return `<!doctype html>
<html lang="en">
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  html { background: ${ground}; }
  body { margin: 0; padding: 1.5rem; display: flex; justify-content: center; }
  pre  { margin: 0; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
         font-size: ${fontSize}; line-height: 1.05; color: ${ink}; }
</style>
<pre>${body}</pre>
</html>
`
}

/* ---------------------------------------------------------------------------
 * The art as a PICTURE, because most places you would put it are not monospace
 *
 * Asked for after looking at what the other converters offer: every one of
 * them hands back a PNG and this tool did not. The reason it matters is not
 * decoration. Text art only survives where the receiving end uses a monospace
 * font and does not reflow -- a README, a terminal, a code block. Paste it
 * into a chat message, a slide, a document or anywhere social and it shears
 * into noise, because a proportional font gives every character a different
 * advance width. Measured in DejaVu Sans at 40px, the classic ramp's
 * characters run from 11.0px to 39.5px across nine distinct widths.
 *
 * A PNG has no such problem, and it is also the only form of this that can be
 * posted anywhere at all.
 *
 * NOTHING GOES NEAR THE NETWORK. The art is already here and it is text, so
 * the picture is built on a canvas in the browser -- the same argument
 * downloadText() and downloadHtml() already make, plus one more: a server
 * round trip could disagree with what is on screen, and this cannot.
 */

/* Where the colours change is decided ONCE, by parseAnsi(), and three
 * renderings ask it: the box, the HTML download and this. That is the rule
 * layoutCaption() exists for on the meme page -- two renderers that agree by
 * hand disagree on some input nobody tried. */

// The stack the box and the HTML download already use, so all three agree
// about which font the picture is in.
export const ASCII_MONO =
    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace'

/* THE CELL IS 2:1, AND THAT IS NOT A STYLE CHOICE -- IT IS THE ONE NUMBER THAT
 * DECIDES WHETHER THE PICTURE IS THE RIGHT SHAPE.
 *
 * jp2a chose the row count on the assumption that a character cell is twice as
 * tall as it is wide: rows = cols * (h/w) * 0.5, which ascii_art.py's header
 * measures at 39/78/129 rows for 60/120/200 columns. Read the other way round,
 * from the rows it actually returns against the source's own size:
 *
 *     1600x1067 photo   60 cols -> 20 rows   cell aspect needed 2.001
 *                      100 cols -> 33 rows                      2.021
 *                      200 cols -> 67 rows                      1.991
 *     434x434 logo      60/100/200 cols                          2.000
 *
 * So painting those rows into anything other than a 2:1 cell reproduces the
 * source at the wrong proportions. The obvious thing to do here -- reuse the
 * box's CSS line-height of 1.05 -- gives a cell of 1.75:1 against a measured
 * 0.6 advance, which SQUASHES every picture to 87.5% of its height. It looks
 * plausible on screen and is wrong on a ruler, which is exactly the class of
 * fault nobody reports.
 *
 * Hence the height is derived from the MEASURED advance rather than from the
 * font size: a monospace whose advance is not 0.6 still gets a square-ish
 * picture. The box keeps its own 1.05 because it is a preview being fitted to
 * a page, not a file anybody measures. */
export const ASCII_CELL_ASPECT = 2

/* 14px, which is a READING size rather than the box's fitting size.
 *
 * The box shrinks its type to 4px at 300 columns so the picture fits on the
 * page without becoming a slot. A file does not have that constraint -- it is
 * opened in a viewer that zooms -- and 4px glyphs rasterise into mush that no
 * amount of zooming recovers. So the PNG is rendered large and the caps below
 * pull it back only when it would exceed what a canvas can hold. */
export const ASCII_PNG_FONT_PX = 14

// Below this the glyphs stop being glyphs, so a render that cannot fit inside
// the caps at this size is refused rather than handed over as grey noise.
const MIN_FONT_PX = 4

// Breathing room, so the outermost characters are not flush against the edge.
const PAD_RATIO = 1.5

/* THE CAPS ARE THE CANVAS'S, NOT THIS FUNCTION'S OPINION.
 *
 * Passed in rather than imported so this module stays free of the transform
 * pipeline, but they are lib/imageTransform.js's MAX_EDGE and MAX_AREA and
 * they exist for the reason CLAUDE.md records: iOS Safari abandons a canvas
 * above about 16.7 MP and returns a BLANK one rather than an error. A 300
 * column render of a tall portrait at 14px is 2,520 x 4,410 -- over the 4,096
 * edge -- so this is reached in ordinary use, not only in the abstract.
 */
export function asciiPngLayout({lines, fontPx = ASCII_PNG_FONT_PX,
                                advanceRatio = 0.6,
                                maxEdge = 4096, maxArea = 12500000} = {}) {
    const rows = lines.length
    const cols = lines.reduce((w, l) => Math.max(w, l.length), 0)
    if (!rows || !cols) {
        return null
    }

    let px = fontPx
    for (;;) {
        const cellW = px * advanceRatio
        const cellH = cellW * ASCII_CELL_ASPECT
        const pad = Math.round(px * PAD_RATIO)
        const width = Math.ceil(cols * cellW) + pad * 2
        const height = Math.ceil(rows * cellH) + pad * 2
        if ((width <= maxEdge && height <= maxEdge && width * height <= maxArea)
            || px <= MIN_FONT_PX) {
            // A picture at the floor that STILL does not fit is not a small
            // picture, it is no picture -- say so rather than handing over a
            // blank canvas, which is exactly what iOS would have done.
            if (width > maxEdge || height > maxEdge || width * height > maxArea) {
                return null
            }
            return {width, height, pad, cellW, cellH, fontPx: px, cols, rows}
        }
        px -= 1
    }
}

/* Split the parsed segments into ROWS, keeping each row's colour stripes.
 *
 * A segment from parseAnsi() can span a newline -- jp2a emits one reset at the
 * end of a line and the next line's first colour after it, so a run of plain
 * characters crosses the break. Painting has to happen a row at a time,
 * because a canvas has no concept of a line, so the newlines are where this
 * cuts rather than where jp2a happened to put an escape.
 */
export function asciiRows(art) {
    const rows = [[]]
    for (const seg of parseAnsi(art)) {
        const parts = String(seg.text).split("\n")
        parts.forEach((part, i) => {
            if (i > 0) {
                rows.push([])
            }
            if (part) {
                rows[rows.length - 1].push({text: part, colour: seg.colour})
            }
        })
    }
    // jp2a ends with a newline, which leaves one empty row that is not part of
    // the picture. Trailing blanks generally: a blank row at the bottom is
    // padding nobody asked for.
    while (rows.length && !rows[rows.length - 1].length) {
        rows.pop()
    }
    return rows
}

/* Paint it. Returns the canvas, or null when there is nothing to paint.
 *
 * The canvas is CREATED here rather than taken as an argument so there is no
 * way to hand this a context whose font, alignment or baseline was set
 * somewhere else -- the bug captionMeasurer() on the meme page exists to
 * prevent, where an inherited ctx.font produced measurements at the wrong
 * scale.
 */
export function drawAsciiPng({art, dark = false, doc = document,
                              fontPx = ASCII_PNG_FONT_PX,
                              maxEdge = 4096, maxArea = 12500000} = {}) {
    const rows = asciiRows(art)
    if (!rows.length) {
        return null
    }
    const lines = rows.map((segs) => segs.map((s) => s.text).join(""))

    const probe = doc.createElement("canvas").getContext("2d")
    probe.font = `${fontPx}px ${ASCII_MONO}`
    /* MEASURED, not assumed to be 0.6. Every monospace has its own advance --
     * Menlo, Consolas and DejaVu Sans Mono do not agree -- and guessing it
     * means the right-hand edge of the picture is either clipped or floating
     * in empty space. `measureText` is asked for a run of ten so a rounding error
     * in one glyph does not become the whole ratio. */
    const advanceRatio = (probe.measureText("MMMMMMMMMM").width / 10) / fontPx
      || 0.6

    const box = asciiPngLayout({lines, fontPx, advanceRatio, maxEdge, maxArea})
    if (!box) {
        return null
    }

    const canvas = doc.createElement("canvas")
    canvas.width = box.width
    canvas.height = box.height
    const ctx = canvas.getContext("2d")

    const ground = dark ? "#000000" : "#ffffff"
    const ink = dark ? "#ffffff" : "#000000"
    ctx.fillStyle = ground
    ctx.fillRect(0, 0, box.width, box.height)

    ctx.font = `${box.fontPx}px ${ASCII_MONO}`
    ctx.textBaseline = "top"
    ctx.textAlign = "left"

    rows.forEach((segs, r) => {
        const y = box.pad + r * box.cellH
        let col = 0
        for (const seg of segs) {
            // PER SEGMENT, NOT PER CHARACTER. Ten thousand fillText calls on a
            // 100x60 render is slow enough to notice; a run shares one call
            // and lands in the same place because the cell is fixed.
            ctx.fillStyle = seg.colour || ink
            ctx.fillText(seg.text, box.pad + col * box.cellW, y)
            col += seg.text.length
        }
    })

    return canvas
}
